import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'

/**
 * ESign6042C — orchestrates the two-step "Response to Notice LTR 6042C
 * Required" signing experience:
 *
 *   1. Reconstructed LTR 6042C — entity-scope, signed once.
 *   2. Cover Letter            — quarter-scope, signed once per affected quarter.
 */
export default function ESign6042C () {
  const navigate = useNavigate()
  const { applicationId } = useParams()

  const [statusLoading, setStatusLoading] = useState(true)
  const [iframeLoading, setIframeLoading] = useState(false)
  const [error, setError] = useState('')
  const [statusData, setStatusData] = useState(null)
  const [signingUrl, setSigningUrl] = useState('')

  const activeStepRef = useRef(null)

  useEffect(() => { fetchStatus() }, [])

  // Poll while the iframe is open — SignWell may not postMessage ESIGN_COMPLETE to the parent.
  useEffect(() => {
    if (!signingUrl) return undefined
    const intervalId = setInterval(() => {
      fetchStatus({ redirectIfDone: true, silent: true })
    }, 4000)
    return () => clearInterval(intervalId)
  }, [signingUrl, applicationId])

  useEffect(() => {
    const handleMessage = async (event) => {
      const payload = event.data
      const type = typeof payload === 'string'
        ? payload
        : payload?.type || payload?.event || ''
      const normalized = String(type).toLowerCase()
      const isComplete = [
        'esign_complete',
        'session_complete',
        'signing_complete',
        'document_signed',
        'completed'
      ].some((t) => normalized.includes(t))

      if (!isComplete) return
      const step = activeStepRef.current
      if (!step) return

      const sessionId = payload?.sessionId || payload?.session_id || ''
      try {
        if (step.type === 'reconstructed') {
          await postAck(`${apiURL}/api/applications/${applicationId}/e-sign/6042c/reconstructed`, sessionId)
        } else if (step.type === 'cover' && step.quarter) {
          await postAck(`${apiURL}/api/applications/${applicationId}/e-sign/6042c/cover?quarter=${step.quarter}`, sessionId)
        }
      } catch (err) {
        console.error('[ESign6042C] ack error:', err)
      }

      setSigningUrl('')
      activeStepRef.current = null
      await fetchStatus({ redirectIfDone: true })
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [applicationId])

  const fetchStatus = async ({ redirectIfDone = false, silent = false } = {}) => {
    if (!silent) setStatusLoading(true)
    if (!silent) setError('')
    try {
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/e-sign/6042c/status`, {
        headers: { 'Authorization': account.accessToken }
      })
      const data = await res.json()

      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshed = await refreshAccessToken()
          if (refreshed) return fetchStatus({ redirectIfDone, silent })
          navigate('/', { state: { expired: true } })
          return
        }
        setError(data.response.error)
        return
      }

      setStatusData(data.response)
      const next = data.response.nextStep
      if (!next || next.type === 'done') {
        setSigningUrl('')
        activeStepRef.current = null
        if (redirectIfDone) {
          navigate(`/applications/${applicationId}/dashboard`)
        }
        return
      }

      activeStepRef.current = next
      if (!signingUrl && !silent) {
        await loadSigningUrl(next)
      }
    } catch (err) {
      if (!silent) setError('Failed to load 6042C signing status. Please try again.')
    } finally {
      if (!silent) setStatusLoading(false)
    }
  }

  const loadSigningUrl = async (step) => {
    setIframeLoading(true)
    setSigningUrl('')
    try {
      const url = step.type === 'reconstructed'
        ? `${apiURL}/api/applications/${applicationId}/e-sign/6042c/reconstructed`
        : `${apiURL}/api/applications/${applicationId}/e-sign/6042c/cover?quarter=${step.quarter}`

      const res = await fetch(url, { headers: { 'Authorization': account.accessToken } })
      const data = await res.json()

      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshed = await refreshAccessToken()
          if (refreshed) return loadSigningUrl(step)
          navigate('/', { state: { expired: true } })
          return
        }
        setError(data.response.error)
        return
      }

      if (data.response.already_signed) {
        const ackUrl = step.type === 'reconstructed'
          ? `${apiURL}/api/applications/${applicationId}/e-sign/6042c/reconstructed`
          : `${apiURL}/api/applications/${applicationId}/e-sign/6042c/cover?quarter=${step.quarter}`
        try {
          await postAck(ackUrl)
        } catch (err) {
          console.error('[ESign6042C] already_signed ack error:', err)
        }
        await fetchStatus({ redirectIfDone: true })
        return
      }

      setSigningUrl(data.response.signing_url || '')
    } catch (err) {
      setError('Failed to load the next signing document. Please try again.')
    } finally {
      setIframeLoading(false)
    }
  }

  const postAck = async (url, sessionId = '') => {
    await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': account.accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId })
    })
  }

  if (statusLoading) {
    return (
      <div className='flex flex-row items-center justify-center gap-2 py-8 transition-colors duration-300'>
        <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
        <p className='text-slate-600 dark:text-slate-300'>Loading…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center gap-4 p-8 transition-colors duration-300'>
        <div className='bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-lg p-6 max-w-md text-center'>
          <p className='text-red-600 dark:text-red-400 font-medium mb-4'>{error}</p>
          <button
            onClick={() => { setError(''); fetchStatus() }}
            className='px-6 py-2 bg-ertc-primary text-white rounded-lg hover:bg-ertc-primary-dark transition-all'
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!statusData) return null

  if (statusData.nextStep?.type === 'done') {
    return (
      <div className='flex flex-col items-center justify-center gap-6 p-8 transition-colors duration-300 max-w-2xl mx-auto'>
        <div className='bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg p-8 text-center'>
          <h2 className='text-2xl font-bold text-emerald-700 dark:text-emerald-400 mb-3'>All signatures complete</h2>
          <p className='text-slate-600 dark:text-slate-300'>
            Thank you. Your Reconstructed LTR 6042C and every quarter cover letter have been signed. Our team will review the package and submit it to the IRS shortly.
          </p>
        </div>
        <button
          onClick={() => navigate(`/applications/${applicationId}/dashboard`)}
          className='px-6 py-2 bg-ertc-primary text-white rounded-lg hover:bg-ertc-primary-dark transition-all'
        >
          Return to dashboard
        </button>
      </div>
    )
  }

  return (
    <div className='w-full flex-1 flex flex-col min-h-0 gap-4'>
      <ProgressBanner statusData={statusData} />
      {iframeLoading && (
        <div className='flex flex-row items-center justify-center gap-2 py-4'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
          <p className='text-slate-600 dark:text-slate-300'>Preparing document…</p>
        </div>
      )}
      {!iframeLoading && signingUrl && (
        <div className='w-full flex-1 min-h-0'>
          <iframe
            src={signingUrl}
            className='w-full h-full border-0 rounded-lg'
            title={iframeTitle(statusData.nextStep)}
            allow='fullscreen'
          />
        </div>
      )}
    </div>
  )
}

function ProgressBanner ({ statusData }) {
  const totalQuarters = statusData.quarters.length
  const signedQuarters = statusData.quarters.filter(q => q.signed).length
  const reconstructedSigned = !!statusData.reconstructed?.signed

  let stepLabel = ''
  if (statusData.nextStep?.type === 'reconstructed') {
    stepLabel = 'Step 1 — sign the Reconstructed LTR 6042C (signed once for your business)'
  } else if (statusData.nextStep?.type === 'cover') {
    const remaining = totalQuarters - signedQuarters
    stepLabel = `Step 2 — sign the cover letter for ${formatQuarter(statusData.nextStep.quarter)} (${remaining} of ${totalQuarters} cover letters remaining)`
  }

  return (
    <div className='shrink-0 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-500/20 rounded-lg p-4 transition-colors duration-300'>
      <p className='font-semibold text-blue-700 dark:text-blue-400'>{stepLabel}</p>
      {statusData.nextStep?.type === 'reconstructed' && (
        <p className='mt-2 text-sm text-slate-600 dark:text-slate-300'>
          Please review the entire form before signing. Fill out every required field and confirm that all business and contact information is accurate.
        </p>
      )}
      <p className='mt-1 text-sm text-slate-600 dark:text-slate-300'>
        Reconstructed LTR: {reconstructedSigned ? '✓ signed' : 'pending'} •
        {' '}Cover letters: {signedQuarters} / {totalQuarters} signed
      </p>
    </div>
  )
}

function iframeTitle (step) {
  if (step?.type === 'reconstructed') return 'E-Sign Reconstructed LTR 6042C'
  if (step?.type === 'cover') return `E-Sign 6042C Cover Letter — ${formatQuarter(step.quarter)}`
  return 'E-Sign 6042C'
}

function formatQuarter (q) {
  if (!q || q.length < 3) return q || ''
  return `Q${q.substring(1, 2)} ${q.substring(2)}`
}
